import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import SkinAnalysisCard from "./components/SkinAnalysisCard";
import SkinAnalysisCardClassic from "./components/SkinAnalysisCardClassic";
import WelcomeScreen from "./components/WelcomeScreen";
import WelcomeScreenB from "./components/WelcomeScreenB";
import ShelfView from "./components/ShelfView";
import ProductDetail from "./components/ProductDetail";
import SkinReadCard from "./components/SkinReadCard";
import ProductAnalysisCard from "./components/ProductAnalysisCard";
import SkinCheckInCard from "./components/SkinCheckInCard";
import ProductChatCard from "./components/ProductChatCard";
import WhyBrandsReview from "./components/WhyBrandsReview";
import ProductAnalysisRedesign from "./components/ProductAnalysisRedesign";
import IngredientChatReviewPreview from "./components/IngredientChatReviewPreview";
import IngredientGlossaryReviewPreview from "./components/IngredientGlossaryReviewPreview";
import "./index.css";
import ProductUIReviewPreview from "./components/ProductUIReviewPreview";
import RoutineAnalysisPreview from "./components/RoutineAnalysisPreview";
import { installAxiosAuthInterceptor } from "./utils/authedRequest";
import { scrubSentryBreadcrumb, scrubSentryEvent } from "./utils/errorReporting";

// Android browsers can enlarge text independently of the layout viewport.
// Keep the resulting layout adjustments scoped to Android web users.
if (typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent)) {
  document.documentElement.classList.add("android-web");
}

// Silence console.log/info/debug in prod; warnings and errors still surface.
if (process.env.NODE_ENV === "production") {
  console.log = console.info = console.debug = () => {};
}

// #63: every axios request to our backend automatically carries the Supabase JWT,
// so a new user-scoped call can't silently ship without the user's token (the
// safety net behind #58). Existing calls that set the header keep theirs.
installAxiosAuthInterceptor();

// ── Dev-only preview routes ─────────────────────────────────────────────────
// Bypass auth + chat + the full photo-analysis flow so we can iterate on UI
// without re-running an LLM each time. Preview values:
//   ?preview=skin-card      → Skin card Prototype B (improved carousel)
//   ?preview=skin-card-b    → Skin card Prototype B
//   ?preview=skin-card-a    → Skin card Prototype A (polished classic)
//   ?preview=welcome-a      → Current live welcome screen (photo)
//   ?preview=welcome-b      → Welcome variant B (visual tiles for 3 features)
// Only triggers when the query param is explicitly set, so safe to ship.
const previewParam =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("preview")
    : null;
const isWhyBrandsReview = process.env.NODE_ENV !== "production" && previewParam === "why-brands-review";
const isRoutineAnalysisPreview = process.env.NODE_ENV !== "production" && previewParam === "routine-analysis-live";

// Sample markdown payload identical in shape to what the live skin-analysis
// backend returns. Keep this in sync if the prompt template changes.
const SAMPLE_SKIN_ANALYSIS = `## Skin Tone
Light skin tone with neutral-to-warm undertones
Your complexion reads fairly balanced (not strongly pink/rosy), with a subtle golden/peach cast across the forehead and cheeks. The neck/chest area also looks consistent in warmth, supporting a neutral-warm read rather than cool.

## Skin Type
Combination
- Noticeable natural sheen on the forehead and upper cheek area (suggesting higher oil production in the centre of the face)
- Cheeks look smooth and hydrated rather than flaky
- Overall texture looks even with a glowy finish

## Confidence
82%

## Detected Concerns
- **Shine/Surface Oiliness** (Severity: LOW, Confidence: 78%): A reflective finish is most noticeable on the forehead and upper cheek, suggesting mild oil breakthrough or very dewy product layering.
- **Mild visible pores (nose/inner cheeks)** (Severity: LOW, Confidence: 72%): Slight pore visibility near the nose/inner cheek area — within a normal range for combination skin.

## Recommendations
- Lightweight, hydrating formulas first; build up only if your skin asks for more
- Niacinamide-containing serums or toners can help with pore appearance and tone
- Daily SPF — your barrier looks healthy, keep it that way

## Disclaimer
This AI analysis is for reference only. For serious skin concerns, consult a dermatologist.
`;

const SAMPLE_USER_PHOTO =
  "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?auto=format&fit=crop&w=400&q=80";

// Structured mock for ?preview=skin-read — the shape the backend will emit
// (bands + cue + zone per dimension; no numbers). Cues written like the real read.
const SAMPLE_SKIN_READ = {
  headline: "Balanced overall — a couple of things to watch",
  tone: "Light", toneHex: "#E7C9AE", undertone: "neutral-warm", skinType: "Combination",
  confidence: "clear",
  standoutStrength: "calm",
  standoutWatch: "evenness",
  dimensions: [
    { key: "calm", name: "Calm", band: "Great", zone: "cheeks", cue: "Skin looks settled — barely any redness through the cheeks, just a touch around the nose." },
    { key: "evenness", name: "Even tone", band: "Fair", zone: "jaw", cue: "A few post-blemish marks along the jaw and some unevenness across the lower cheeks." },
    { key: "oil", name: "Oil balance", band: "Fair", zone: "t-zone", cue: "A soft sheen sits across the forehead and nose — an oilier T-zone, while the cheeks read calmer." },
    { key: "hydration", name: "Hydration", band: "Good", zone: "overall", cue: "Light sits softly and evenly overall — no obvious tightness or flaking." },
    { key: "texture", name: "Texture", band: "Good", zone: "overall", cue: "Mostly smooth, with a little visible texture through the central face." },
    { key: "clarity", name: "Clarity", band: "Good", zone: "overall", cue: "Clear right now — nothing actively breaking out." },
    { key: "radiance", name: "Radiance", band: "Good", zone: "overall", cue: "A healthy light return; indoor daylight makes this a softer read." },
  ],
};

// Sample shelf data for the ?preview=shelf route (#55 redesign). Mix of makeup
// (with shade) + skincare, some with images + some without (Package fallback).
const SAMPLE_SHELF = {
  current: [
    { id: "l1", name: "Neo Cushion Glow", brand: "Laneige", recommended_shade: "[Glow] 21N1 Neutral", swatch_hex: "#D0A87E", image: "https://images.unsplash.com/photo-1631730359585-38a4935cbec4?auto=format&fit=crop&w=400&q=70" },
    { id: "b1", name: "Glow Serum · Propolis + Niacinamide", brand: "Beauty of Joseon", image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=400&q=70" },
    { id: "r1", name: "Soft Pinch Liquid Blush", brand: "Rare Beauty", recommended_shade: "Hope", swatch_hex: "#C97F8A" },
    { id: "a1", name: "Heartleaf 77% Soothing Toner", brand: "Anua" },
    { id: "n1", name: "Light Reflecting Foundation", brand: "Nars", recommended_shade: "Punjab", swatch_hex: "#CBA079" },
    { id: "rl1", name: "Birch Juice Moisturizing Sunscreen", brand: "Round Lab" },
  ],
  wishlist: [
    { id: "ct1", name: "Matte Revolution Lipstick", brand: "Charlotte Tilbury", recommended_shade: "Pillow Talk", swatch_hex: "#C97F72" },
    { id: "t1", name: "Dive-In Low Molecular Hyaluronic Serum", brand: "Torriden" },
  ],
};

// Sample products for ?preview=product-detail (#55) — makeup (shade + attributes)
// and skincare (benefits), each with structured ingredients + ai_analysis.
const SAMPLE_DETAIL_MAKEUP = {
  id: "m1", name: "Neo Cushion Glow", brand: "Laneige", list_category: "current",
  image: "https://images.unsplash.com/photo-1631730359585-38a4935cbec4?auto=format&fit=crop&w=720&q=70",
  recommended_shade: "[Glow] 21N1 Neutral", swatch_hex: "#D8B48C",
  finish: "Glow finish", coverage: "Medium", spf: "42",
  shade_trio: [
    { shade_name: "13N", hex: "#E8CBA6" },
    { shade_name: "21N1", hex: "#D8B48C", is_match: true },
    { shade_name: "23W", hex: "#C79A6E" },
  ],
  ingredients: [
    { name: "SPF 42 PA++", benefit: "Daily UV defence built in" },
    { name: "Niacinamide", benefit: "Brightens tone with wear" },
    { name: "Hyaluronic acid", benefit: "Keeps it dewy, not cakey" },
  ],
  usage_tags: ["in_rotation", "seasonal"],
  notes: "One layer for day, two for evening. Sets better over the Centella serum.",
  ai_analysis: { reasoning: "Sits true to your light-neutral depth, and the dewy finish flatters combination skin without emphasising your T-zone shine.", compatibility_score: 88 },
};
const SAMPLE_DETAIL_SKINCARE = {
  id: "s1", name: "Glow Serum · Propolis + Niacinamide", brand: "Beauty of Joseon", list_category: "current",
  image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=720&q=70",
  benefits: ["Brightening", "Soothing", "Hydrating"],
  ingredients: [
    { name: "Propolis 60%", benefit: "Soothes + supports the barrier" },
    { name: "Niacinamide 2%", benefit: "Brightens + evens tone" },
    { name: "Beta-glucan", benefit: "Lightweight, calming hydration" },
  ],
  usage_tags: ["in_rotation", "for_concerns"],
  notes: "Every AM after toner — tone looks brighter after ~3 weeks.",
  ai_analysis: { reasoning: "A gentle brightening step that layers easily under everything else — good for the uneven tone you're tracking, without overwhelming combination skin." },
};

const sentryEnvironment = (() => {
  const configuredEnvironment = process.env.REACT_APP_ENV?.trim().toLowerCase();
  if (configuredEnvironment === "prod") return "production";
  if (configuredEnvironment === "dev") return "development";
  if (configuredEnvironment) return configuredEnvironment;
  if (typeof window === "undefined") return process.env.NODE_ENV || "unknown";
  if (window.location.hostname === "app.sonmi.io") return "production";
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") return "development";
  return "preview";
})();
const suppressSentry = (() => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("e2e") === "1" || Boolean(params.get("preview"));
})();

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: sentryEnvironment,
  enabled: Boolean(process.env.REACT_APP_SENTRY_DSN) && !suppressSentry,
  sendDefaultPii: false,
  // Capture 10% of sessions for performance tracing (free-tier friendly)
  tracesSampleRate: 0.1,
  // Replay 10% of sessions, 100% when an error occurs
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Chats can include faces and deeply personal skin information. Replays
      // retain layout/interaction evidence while masking every user value.
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
  ],
  beforeBreadcrumb: scrubSentryBreadcrumb,
  beforeSend: scrubSentryEvent,
  // Don't send events for these noisy browser errors
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Non-Error exception captured",
    "Network request failed",
  ],
});

const root = createRoot(document.getElementById("root"));

// ── Preview route renders ──────────────────────────────────────────────────
const isPrototypeA = previewParam === "skin-card-a";
const isPrototypeB = previewParam === "skin-card" || previewParam === "skin-card-b";
const isWelcomeA = previewParam === "welcome-a";
const isWelcomeB = previewParam === "welcome-b";
const isShelf = previewParam === "shelf";
const isProductDetail = previewParam === "product-detail";
const isSkinRead = previewParam === "skin-read";
const isProductCard = previewParam === "product-card";
const isProductChat = previewParam === "product-chat";
const isSkinCheckin = previewParam === "skin-checkin";
const isCheckinFlow = previewParam === "checkin-flow";
const isProductUIReview = (process.env.NODE_ENV !== "production" || process.env.REACT_APP_PRODUCT_UI_REVIEW === "true") && previewParam === "product-ui-review";
const isFullInciReview = process.env.NODE_ENV !== "production" && previewParam === "full-inci-review";
const isIngredientChatReview = process.env.NODE_ENV !== "production" && previewParam === "ingredient-chat-review";
const isIngredientGlossaryReview = process.env.NODE_ENV !== "production" && previewParam === "ingredient-glossary-review";

// Redesigned in-chat product card (mirrors the menu "Analyse a Product" view),
// with the trimmed chat-length copy.
const SAMPLE_PRODUCT_CHAT = {
  name: "Bifida Biome Complex Ampoule",
  brand: "ma:nyo",
  category: "skincare",
  image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=400&q=70",
  score: 82,
  scoring: [
    { label: "Your concerns", score: 82, reason: "Helps uneven tone, mild redness and texture, but offers limited pore control." },
    { label: "Skin type", score: 84, reason: "Watery and humectant-rich, it should hydrate combination skin without heaviness." },
    { label: "Ingredient safety", score: 82, reason: "No declared sensitivities flagged, though witch hazel may irritate redness-prone skin." },
    { label: "Formulation", score: 78, reason: "Niacinamide ranks high with several ferments, but the product match isn't exact." },
  ],
  keyIngredients: ["Methylpropanediol", "Niacinamide", "Bifida Ferment Lysate (10,104ppm)", "Lactobacillus Ferment Lysate", "Lactococcus Ferment Lysate", "Saccharomyces Ferment Filtrate"],
  keyIngredientsMore: 4,
  formulation: [
    { lead: "Methylpropanediol —", rest: "Very high — a hydrating solvent that improves slip and ingredient delivery." },
    { lead: "Niacinamide —", rest: "High — likely enough to support tone, barrier and pore appearance." },
    { lead: "Bifida Ferment Lysate (10,104ppm) —", rest: "Mid-high — supports the barrier and conditions skin." },
    { lead: "Lactobacillus Ferment Lysate —", rest: "After bifida — a postbiotic-style conditioner supporting barrier resilience." },
    { lead: "Glycyrrhiza Glabra (Licorice) Root Extract —", rest: "Mid-list — helps soothe and even visible tone." },
  ],
  whatItMeans: [
    { tone: "good", text: "Ferments, niacinamide and liquorice suit mild redness and uneven texture." },
    { tone: "good", text: "A lightweight hydrating layer for combination skin, rather than a rich cream." },
    { tone: "good", text: "Already in your AM routine, it supports rather than conflicts with existing steps." },
    { tone: "good", text: "Pairs well with sunscreen and moisturiser through hydration and barrier support." },
    { tone: "caution", text: "Several routine products already contain niacinamide, so benefits may overlap rather than add something new." },
  ],
};

// Sample data for the two card previews (?preview=product-card / skin-checkin).
// The product `data` matches EXACTLY what the deployed backend sends and what
// ChatComponent maps into msg.text.data (see ChatComponent.jsx ~1745). Only
// these fields are sent — so no Compatibility Breakdown / Alternatives / price
// / action buttons render, matching the live in-chat card.
const SAMPLE_PRODUCT_ANALYSIS = {
  score: 82,
  compatibility: "high",
  whatsGreat: [
    "Lightweight hydration that suits combination skin",
    "Snail mucin supports barrier repair without sitting heavy",
  ],
  concerns: ["No active brightening — layer a separate serum if that's a goal"],
  reasoning: "Its humectant-led formula matches your combination skin and barrier focus.",
  best_for: "Daily hydration on combination or barrier-focused skin",
  ingredient_insight:
    "Snail Secretion Filtrate sits high in the formula, providing lightweight hydration and barrier support. Betaine adds a humectant boost without heaviness. Sodium Hyaluronate anchors water into the skin for a plumping effect.",
  key_ingredients: ["Snail Secretion Filtrate", "Betaine", "Sodium Hyaluronate"],
  productType: "Skincare",
  recommendation: {
    verdict: "recommended",
    reasoning: "Its humectant-led formula matches your combination skin and barrier focus.",
    usage: "Daily, AM/PM, after toner",
  },
};

// Matches the `skinCheckin` payload contract (see skinCheckinSchema.js).
// Variants via ?v= : default | update | hard
const CHECKIN_CHANGES = [
  { concern: "T-zone shine", direction: "improving", word: "Easing",
    evidence: "Less shine across the forehead and nose than your last scan — the mattifying step is holding." },
  { concern: "Cheek dryness", direction: "improving", word: "Better",
    evidence: "The flaking along both cheeks has settled; the surface looks more even than six days ago." },
  { concern: "Forehead texture", direction: "steady", word: "Steady",
    evidence: "Much the same as last time — texture like this usually shifts over weeks, not days." },
  { concern: "chin breakout", direction: "flaring", word: "Flaring", isNew: true,
    evidence: "Two new raised spots along the jawline that weren't in your last scan." },
];

const SAMPLE_CHECKIN = {
  number: 3,
  sinceLabel: "your last scan, 6 days ago",
  comparability: { ok: true },
  changes: CHECKIN_CHANGES,
  profileUpdates: [],
  assessment:
    "Calmer than your first scan — the dryness is settling. Keep the routine, ease off exfoliation this week, and watch the chin.",
};

// The case Sofia raised: the first scan's undertone was a low-confidence read
// in poor light. A clearer scan corrects it — that's news, so we surface it.
const SAMPLE_CHECKIN_UPDATE = {
  ...SAMPLE_CHECKIN,
  profileUpdates: [
    { field: "undertone", from: "neutral", to: "warm-neutral",
      why: "Your first scan was in warm indoor light, which muddied the read. This one's clearer." },
  ],
};

const SAMPLE_CHECKIN_HARD = {
  ...SAMPLE_CHECKIN,
  comparability: { ok: false, reason: "there's makeup on and your baseline was bare skin" },
  changes: [],
  assessment: "",
};

// A same-day re-scan: no real progression in hours, so we skip the delta and
// show a calm steady note instead of flip-flopping per-dimension directions.
const SAMPLE_CHECKIN_STEADY = {
  sinceLabel: "your last scan, earlier today",
  comparability: { ok: true },
  changes: [],
  profileUpdates: [],
  steady: true,
};

if (isRoutineAnalysisPreview) {
  root.render(<RoutineAnalysisPreview />);
} else if (isWhyBrandsReview) {
  root.render(<React.StrictMode><WhyBrandsReview /></React.StrictMode>);
} else if (isIngredientGlossaryReview) {
  root.render(<IngredientGlossaryReviewPreview />);
} else if (isIngredientChatReview) {
  root.render(<IngredientChatReviewPreview />);
} else if (isFullInciReview) {
  root.render(
    <React.StrictMode>
      <div style={{ width: '100%', maxWidth: 430, height: '100dvh', margin: '0 auto', fontFamily: "'DM Sans', sans-serif" }}>
        <ProductAnalysisRedesign
          glossaryPreview
          result={{
            product: {
              name: 'REJURAN Derma Healer Pore Tightening Ampoule', brand: 'REJURAN', category: 'Skincare',
              keyIngredients: ['Aloe Barbadensis Leaf Juice', 'Niacinamide', 'Gluconolactone'],
              formulation_bullets: [
                { ingredient: 'Aloe Barbadensis Leaf Juice', note: 'High in the INCI after humectants, for lightweight hydration.' },
                { ingredient: 'Niacinamide', note: 'High in the INCI, supporting tone and barrier function.' },
                { ingredient: 'Gluconolactone', note: 'A PHA that provides gentle exfoliation.' },
              ],
              ingredientsFull: 'Water, Butylene Glycol, Glycerin, Aloe Barbadensis Leaf Juice, Cucumis Sativus (Cucumber) Fruit Extract, Niacinamide, 1,2-Hexanediol, Hamamelis Virginiana (Witch Hazel) Extract, Gluconolactone, Lactic Acid, Hydrolyzed DNA, Dipropylene Glycol, Caprylic/Capric Triglyceride, Glycolic Acid, Sodium Hydroxide, Sodium Hyaluronate, Xanthan Gum, Avena Sativa (Oat) Kernel Extract, Calendula Officinalis Extract, Propanediol, Caramel, White Willow Bark Extract, Laminaria Ochroleuca Extract, Salicylic Acid, Allantoin, Disodium EDTA, Ethylhexylglycerin, Acacia Senegal Gum, Adenosine, Pentylene Glycol, Sodium Mannose Phosphate, Mannose, Lysine, Citrus Aurantium Bergamia (Bergamot) Fruit Oil, Lavandula Angustifolia (Lavender) Oil, Proline, Copper Sulfate, Achillea Millefolium Extract, Capryloyl Salicylic Acid, Citrus Paradisi (Grapefruit) Peel Oil',
            },
            matchScore: 70,
            analysis: { pros: ['Good match for your visible pores and uneven texture concerns.'],
              cons: ['Your dry skin and mild redness may not tolerate the combination of acids, witch hazel, and essential oils well.'] },
            recommendation: { usage: 'Consider using on non-retinoid nights.' },
          }}
          onBack={() => {}} onAnalyseAnother={() => {}} onAsk={() => {}} onSaveRequest={() => {}}
        />
      </div>
    </React.StrictMode>
  );
} else if (isProductUIReview) {
  root.render(<ProductUIReviewPreview />);
} else if (isWelcomeA || isWelcomeB) {
  const Component = isWelcomeA ? WelcomeScreen : WelcomeScreenB;
  const label = isWelcomeA
    ? "Preview · Welcome A (current live)"
    : "Preview · Welcome B (visual tiles)";
  const otherLink = isWelcomeA
    ? { href: "/?preview=welcome-b", text: "→ See Welcome B (visual tiles)" }
    : { href: "/?preview=welcome-a", text: "→ See Welcome A (current live)" };

  const noop = (which) => () => {
    // eslint-disable-next-line no-alert
    alert(`CTA: ${which}`);
  };

  root.render(
    <React.StrictMode>
      <div style={{ position: "relative", minHeight: "100dvh" }}>
        <div style={{
          position: "fixed",
          top: 8,
          left: 0,
          right: 0,
          zIndex: 100,
          textAlign: "center",
          pointerEvents: "none",
        }}>
          <span style={{
            display: "inline-block",
            fontSize: 13,
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            color: "#2A1212",
            background: "rgba(247, 241, 234, 0.85)",
            padding: "4px 10px",
            borderRadius: 999,
            fontFamily: "'DM Sans', Arial, sans-serif",
            fontWeight: 600,
            pointerEvents: "auto",
          }}>
            {label} ·{" "}
            <a href={otherLink.href} style={{ color: "#7A1F1F", textDecoration: "underline" }}>
              swap
            </a>
          </span>
        </div>
        <Component
          onCreateAccount={noop("Create account")}
          onSignIn={noop("Sign in")}
          onContinueAsGuest={noop("Continue as guest")}
        />
      </div>
    </React.StrictMode>
  );
} else if (isPrototypeA || isPrototypeB) {
  const CardComponent = isPrototypeA ? SkinAnalysisCardClassic : SkinAnalysisCard;
  const label = isPrototypeA
    ? "Preview · Prototype A (polished classic)"
    : "Preview · Prototype B (improved carousel)";
  const otherLink = isPrototypeA
    ? { href: "/?preview=skin-card-b", text: "→ See Prototype B (carousel)" }
    : { href: "/?preview=skin-card-a", text: "→ See Prototype A (classic)" };

  root.render(
    <React.StrictMode>
      <div style={{
        minHeight: "100vh",
        background: "#F2EAE1",  // brand cream backdrop so the white cards pop
        padding: "32px 16px",
        fontFamily: "'DM Sans', Arial, sans-serif",
      }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <p style={{
            fontSize: 13,
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            color: "#2A324B99",
            textAlign: "center",
            marginBottom: 6,
            fontWeight: 600,
          }}>
            {label}
          </p>
          <p style={{ textAlign: "center", marginBottom: 20 }}>
            <a href={otherLink.href} style={{
              fontSize: 13,
              color: "#2A324B",
              textDecoration: "underline",
              textUnderlineOffset: 4,
            }}>{otherLink.text}</a>
          </p>
          <CardComponent
            analysis={SAMPLE_SKIN_ANALYSIS}
            imageUrl={SAMPLE_USER_PHOTO}
            isScan={true}
            photoCount={4}
            angleImages={[SAMPLE_USER_PHOTO, SAMPLE_USER_PHOTO, SAMPLE_USER_PHOTO, SAMPLE_USER_PHOTO]}
            userId={null}
            isAnonymous={false}
            insight="Your barrier looks stronger than average for combination skin — but there's one thing around your eyes I'd keep an eye on."
            onReadFeedback={(v) => console.log("read_feedback:", v)}
            onCorrectionSubmit={(p) => alert("correction: " + JSON.stringify(p))}
            onAnalyseProduct={() => alert("CTA: Analyse a product")}
            onSeeShadeMatches={() => alert("CTA: See shade matches")}
            onAddRoutine={() => alert("CTA: Add your routine")}
          />
        </div>
      </div>
    </React.StrictMode>
  );
} else if (isProductDetail) {
  root.render(
    <React.StrictMode>
      <div style={{ minHeight: "100vh", background: "#FAF7F2", padding: "20px 0", fontFamily: "'DM Sans', sans-serif" }}>
        <p style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.15em", color: "#2A324B99", textAlign: "center", marginBottom: 12, fontWeight: 600 }}>
          Preview · Product detail (#55)
        </p>
        <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 15px", display: "flex", flexDirection: "column", gap: 26 }}>
          <ProductDetail product={SAMPLE_DETAIL_MAKEUP} sourceListId="current"
            onBack={() => {}} onSave={() => {}} onDelete={() => {}}
            onCompare={() => alert("Compare")} onAddToRoutine={() => alert("Add to routine")}
            onReMatch={() => alert("Re-match")} userId={null} />
          <ProductDetail product={SAMPLE_DETAIL_SKINCARE} sourceListId="current"
            onBack={() => {}} onSave={() => {}} onDelete={() => {}}
            onCompare={() => alert("Compare")} onAddToRoutine={() => alert("Add to routine")}
            userId={null} />
        </div>
      </div>
    </React.StrictMode>
  );
} else if (isSkinRead) {
  root.render(
    <React.StrictMode>
      <div style={{ minHeight: "100vh", background: "#F2EAE1", padding: "28px 16px", fontFamily: "'DM Sans', sans-serif" }}>
        <p style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.15em", color: "#2A324B99", textAlign: "center", marginBottom: 18, fontWeight: 600 }}>
          Preview · Skin read (face-evidence)
        </p>
        <div style={{ maxWidth: 380, margin: "0 auto" }}>
          <SkinReadCard read={SAMPLE_SKIN_READ} imageUrl={SAMPLE_USER_PHOTO} />
        </div>
      </div>
    </React.StrictMode>
  );
} else if (isShelf) {
  root.render(
    <React.StrictMode>
      <div style={{ minHeight: "100vh", background: "#FAF7F2", padding: "20px 0", fontFamily: "'DM Sans', sans-serif" }}>
        <p style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.15em", color: "#2A324B99", textAlign: "center", marginBottom: 12, fontWeight: 600 }}>
          Preview · Shelf (#55)
        </p>
        <div style={{ maxWidth: 400, margin: "0 auto", background: "#fff", borderRadius: 26, border: "1px solid #EAE0D6", padding: "16px 15px 20px" }}>
          <ShelfView
            products={SAMPLE_SHELF}
            onSelectProduct={(p) => alert("Open detail: " + p.name)}
            onMenuProduct={(p) => alert("Menu: " + p.name)}
            onOpenRoutine={() => alert("Open routine")}
          />
        </div>
      </div>
    </React.StrictMode>
  );
} else if (isProductCard) {
  root.render(
    <React.StrictMode>
      <div style={{ minHeight: "100vh", background: "#F2EAE1", padding: "28px 16px", fontFamily: "'DM Sans', sans-serif" }}>
        <p style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.15em", color: "#2A324B99", textAlign: "center", marginBottom: 18, fontWeight: 600 }}>
          Preview · Product card (in-chat) + &ldquo;See full analysis&rdquo; CTA
        </p>
        <div style={{ maxWidth: 380, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
          <ProductAnalysisCard
            analysis={SAMPLE_PRODUCT_ANALYSIS}
            productName="Advanced Snail 96 Mucin Power Essence"
            productBrand="COSRX"
            productImage="https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=720&q=70"
            onSave={() => alert("Save to…")}
            customListNames={{}}
            onSeeFullAnalysis={() => alert("Open full analysis (rich menu view)")}
          />
        </div>
      </div>
    </React.StrictMode>
  );
} else if (isProductChat) {
  root.render(
    <React.StrictMode>
      <div style={{ minHeight: "100vh", background: "#F2EAE1", padding: "28px 16px", fontFamily: "'DM Sans', sans-serif" }}>
        <p style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.15em", color: "#2A324B99", textAlign: "center", marginBottom: 6, fontWeight: 600 }}>
          Preview · In-chat product card — free vs unlocked
        </p>
        <p style={{ fontSize: 12.5, color: "#2A324B99", textAlign: "center", marginBottom: 22, maxWidth: 620, marginLeft: "auto", marginRight: "auto" }}>
          Left: what a guest sees (score + ingredients free; scoring/meaning behind a free account; formulation behind Premium).
          Right: the unlocked card.
        </p>
        <div style={{ display: "flex", gap: 28, justifyContent: "center", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 360, maxWidth: "100%" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#2A324B", textAlign: "center", marginBottom: 10 }}>Guest — gated</p>
            <ProductChatCard product={SAMPLE_PRODUCT_CHAT} access="guest" />
          </div>
          <div style={{ width: 360, maxWidth: "100%" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#2A324B", textAlign: "center", marginBottom: 10 }}>Unlocked — full</p>
            <ProductChatCard product={SAMPLE_PRODUCT_CHAT} access="full" />
          </div>
        </div>
      </div>
    </React.StrictMode>
  );
} else if (isCheckinFlow) {
  // Sofia's model (2026-08): ANSWERING and TRACKING are separate jobs.
  //   · bare photo        -> the tracker module IS the reply
  //   · photo + question  -> answer + reasoning as normal prose, tracker below
  // Rendered in the real chat frame: white list, cream user bubble carrying just
  // the photo, assistant prose full-width with no avatar.
  const Bubble = ({ text }) => (
    <div style={{ marginLeft: "auto", width: "fit-content", maxWidth: "75%", background: "#FDF6EE",
                  border: "1px solid rgba(243,213,181,0.4)", borderRadius: 16,
                  padding: "12px 16px", marginBottom: 16, fontSize: 14, lineHeight: 1.5, color: "#2A1212" }}>
      {text && <div style={{ marginBottom: 8 }}>{text}</div>}
      <img src={SAMPLE_USER_PHOTO} alt="Uploaded"
           style={{ maxWidth: "200px", width: "100%", display: "block", borderRadius: "8px" }} />
    </div>
  );
  const Prose = ({ children }) => (
    <div style={{ color: "#2A324B", fontSize: 15, lineHeight: 1.55, padding: "0 4px", marginBottom: 12 }}>
      {children}
    </div>
  );
  const Case = ({ label }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "34px 0 18px" }}>
      <span style={{ flex: 1, height: 1, background: "rgba(52,28,28,0.10)" }} />
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
                     color: "rgba(42,50,75,0.38)", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ flex: 1, height: 1, background: "rgba(52,28,28,0.10)" }} />
    </div>
  );
  root.render(
    <React.StrictMode>
      <div style={{ height: "100vh", overflowY: "auto", background: "#fff", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ background: "#341C1C", color: "#fff", padding: "14px 20px", display: "flex",
                      alignItems: "center", justifyContent: "center", gap: 6, position: "sticky", top: 0, zIndex: 5 }}>
          <span style={{ color: "#F3D5B5", fontSize: 13 }}>&#10022;</span>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: "0.025em" }}>Son:mi</span>
        </div>
        <div style={{ maxWidth: 430, margin: "0 auto", padding: "12px 16px 48px" }}>

          <Case label="Photo, no question &mdash; tracker is the reply" />
          <Bubble />
          <SkinCheckInCard checkin={SAMPLE_CHECKIN}
            onOpenJournal={() => alert("Open Skin Journal")} />

          <Case label="Photo + a question &mdash; answer first, tracker below" />
          <Bubble text="what's going on with my chin?" />
          <Prose>
            That&rsquo;s two small raised spots along the jawline &mdash; they weren&rsquo;t in your last
            scan six days ago. They read as congestion rather than active inflammation:
            the surrounding skin isn&rsquo;t red or warm-looking.
            <br /><br />
            Most likely friction or a richer product settling in. I&rsquo;d hold off on
            anything new this week, keep the area clean, and let it settle rather than
            treating it aggressively &mdash; it should calm on its own.
          </Prose>
          <SkinCheckInCard checkin={SAMPLE_CHECKIN}
            onOpenJournal={() => alert("Open Skin Journal")} />

          <Case label="A reading improved" />
          <Bubble />
          <SkinCheckInCard checkin={SAMPLE_CHECKIN_UPDATE}
            onOpenJournal={() => alert("Open Skin Journal")}
            onConfirmUpdate={(u) => alert("Correction flow: " + u.field)} />

          <p style={{ fontSize: 12, color: "rgba(42,50,75,0.45)", textAlign: "center",
                      marginTop: 26, lineHeight: 1.5 }}>
            Tap any row for the evidence behind the call.
          </p>
        </div>
      </div>
    </React.StrictMode>
  );
} else if (isSkinCheckin) {
  // Rendered in the REAL chat context — white list, cream user bubble, no
  // avatar — so it's judged the way a user actually meets it.
  const variant = new URLSearchParams(window.location.search).get("v") || "default";
  const data = variant === "update" ? SAMPLE_CHECKIN_UPDATE
             : variant === "hard"   ? SAMPLE_CHECKIN_HARD
             : variant === "steady" ? SAMPLE_CHECKIN_STEADY
             : SAMPLE_CHECKIN;
  const TABS = [
    ["default", "Normal check-in"],
    ["update", "Profile updated"],
    ["hard", "Hard to compare"],
    ["steady", "Same-day (steady)"],
  ];
  root.render(
    <React.StrictMode>
      <div style={{ minHeight: "100vh", background: "#fff", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ background: "#341C1C", color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <span style={{ color: "#F3D5B5", fontSize: 13 }}>✦</span>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: "0.025em" }}>Son:mi</span>
        </div>
        {/* PREVIEW SCAFFOLDING — never ships. Styled deliberately unlike the
            app so it can't be mistaken for part of the card. */}
        <div style={{ background: "#EFEBE6", borderBottom: "1px solid rgba(42,50,75,0.10)",
                      padding: "8px 12px 10px" }}>
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase",
                      color: "rgba(42,50,75,0.40)", textAlign: "center", margin: "0 0 6px" }}>
            Preview states · not in the app
          </p>
          <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
            {TABS.map(([v, label]) => (
              <a key={v} href={`/?preview=skin-checkin&v=${v}`}
                 style={{ fontSize: 12, fontWeight: 600, textDecoration: "none", borderRadius: 4,
                          padding: "4px 9px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          border: "1px solid rgba(42,50,75,0.18)",
                          background: variant === v ? "rgba(42,50,75,0.85)" : "transparent",
                          color: variant === v ? "#fff" : "rgba(42,50,75,0.6)" }}>{label}</a>
            ))}
          </div>
        </div>
        <div style={{ maxWidth: 430, margin: "0 auto", padding: "16px 16px 40px" }}>
          {/* Faithful to ChatUI: the scan's boilerplate prompt ("I uploaded a
              photo for analysis") is stripped from display, so the user bubble
              carries just the photo — img maxWidth 200, radius 8. */}
          <div style={{ marginLeft: "auto", width: "fit-content", maxWidth: "75%", background: "#FDF6EE",
                        border: "1px solid rgba(243,213,181,0.4)", borderRadius: 16,
                        padding: "12px 16px", fontSize: 14, lineHeight: 1.5, color: "#2A1212", marginBottom: 16 }}>
            <img
              src={SAMPLE_USER_PHOTO}
              alt="Uploaded"
              style={{ maxWidth: "200px", width: "100%", display: "block", borderRadius: "8px" }}
            />
          </div>
          <SkinCheckInCard
            checkin={data}
            onOpenJournal={() => alert("Open Skin Journal (SkinTimeline)")}
            onConfirmUpdate={(u) => alert("Correction flow for: " + u.field)}
          />
          <p style={{ fontSize: 12, color: "rgba(42,50,75,0.45)", textAlign: "center", marginTop: 20, lineHeight: 1.5 }}>
            Tap any concern row to see the evidence behind the call.
          </p>
        </div>
      </div>
    </React.StrictMode>
  );
} else {
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary
        fallback={
        <div style={{
          minHeight: "100vh",
          background: "#F2EAE1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 12,
          fontFamily: "'DM Sans', Arial, sans-serif",
          color: "#341C1C",
          padding: 24,
          textAlign: "center",
        }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#F2EAE1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" fill="none" stroke="#341C1C" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Something went wrong</h2>
          <p style={{ fontSize: 14, opacity: 0.7, margin: 0 }}>
            We've been notified and are looking into it. Please refresh to continue.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 8,
              padding: "10px 24px",
              background: "#341C1C",
              color: "white",
              border: "none",
              borderRadius: 24,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>
      }
      showDialog={false}
    >
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
}
